import {
  usePaginatedResource,
  useResourceItem,
  useCreateResource,
  useEditResource,
  useDeleteResource,
} from "./useCrudResource"

import {
  getAccessoriesUsageList,
  getAccessoriesUsageById,
  createAccessoriesUsage,
  updateAccessoriesUsage,
  deleteAccessoriesUsage,
  getTrashedAccessoriesUsages,
  restoreAccessoriesUsage,
  forceDeleteAccessoriesUsage,
} from "api/accessoriesUsageApi"

import { useIsFetching, useMutation, useQueryClient } from "@tanstack/react-query"

export const ACCESSORIES_USAGE_QUERY_KEY = "accessories-usages"

// -------------------- FETCH PAGINATED --------------------
export const useAccessoriesUsages = (params = {}, enabled = true) => {
  return usePaginatedResource(
    ACCESSORIES_USAGE_QUERY_KEY,
    getAccessoriesUsageList,
    params,
    enabled
  )
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useAccessoriesUsage = (id, enabled = true) => {
  return useResourceItem(
    ACCESSORIES_USAGE_QUERY_KEY,
    getAccessoriesUsageById,
    id,
    enabled
  )
}


// -------------------- CREATE --------------------
export const useCreateAccessoriesUsage = () => {
  return useCreateResource(
    createAccessoriesUsage,
    ACCESSORIES_USAGE_QUERY_KEY
  )
}

// -------------------- EDIT --------------------
export const useEditAccessoriesUsage = () => {
  return useEditResource(
    updateAccessoriesUsage,
    ACCESSORIES_USAGE_QUERY_KEY
  )
}

// -------------------- DELETE (Soft Delete) --------------------
export const useDeleteAccessoriesUsage = () => {
  return useDeleteResource(
    deleteAccessoriesUsage,
    ACCESSORIES_USAGE_QUERY_KEY
  )
}

// -------------------- FORCE DELETE (Permanent Delete) --------------------
export const useForceDeleteAccessoriesUsage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: forceDeleteAccessoriesUsage,
    onSuccess: (_, id) => {
      // Remove from trashed cache
      queryClient.setQueriesData(
        [ACCESSORIES_USAGE_QUERY_KEY, "trashed"],
        old => {
          if (!old?.data?.data) return old
          const updated = structuredClone(old)
          updated.data.data = updated.data.data.filter(item => item.id !== id)
          updated.data.total = Math.max(0, (updated.data.total || 1) - 1)
          return updated
        }
      )

      // Remove individual cache
      queryClient.removeQueries([ACCESSORIES_USAGE_QUERY_KEY, id])
    },
  })
}

// -------------------- ADDITIONAL --------------------

// ✅ Get all (no pagination)
export const useAllAccessoriesUsages = (params = {}) => {
  return usePaginatedResource(
    ACCESSORIES_USAGE_QUERY_KEY,
    getAccessoriesUsageList,
    { ...params, all: true },
    true
  )
}

// ✅ Get trashed (soft-deleted)
export const useTrashedAccessoriesUsages = (params = {}) => {
  return usePaginatedResource(
    [ACCESSORIES_USAGE_QUERY_KEY, "trashed"],
    getTrashedAccessoriesUsages,
    params,
    true
  )
}

// ✅ Restore trashed item
export const useRestoreAccessoriesUsage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: restoreAccessoriesUsage,
    onSuccess: (res, id) => {
      const restoredItem = res?.data
      if (!restoredItem) return

      // Remove from trashed cache
      queryClient.setQueriesData(
        [ACCESSORIES_USAGE_QUERY_KEY, "trashed"],
        old => {
          if (!old?.data?.data) return old
          const updated = structuredClone(old)
          updated.data.data = updated.data.data.filter(item => item.id !== id)
          updated.data.total = Math.max(0, (updated.data.total || 1) - 1)
          return updated
        }
      )

      // Add to main list cache
      queryClient.setQueriesData(
        [ACCESSORIES_USAGE_QUERY_KEY],
        old => {
          if (!old?.data?.data) return old
          const updated = structuredClone(old)
          updated.data.data = [restoredItem, ...updated.data.data]
          updated.data.total = (updated.data.total || 0) + 1
          return updated
        }
      )

      // Update individual cache
      queryClient.setQueryData(
        [ACCESSORIES_USAGE_QUERY_KEY, id],
        restoredItem
      )
    },
  })
}

// -------------------- SEARCH (PROGRAMMATIC) --------------------
export const useAccessoriesUsageSearchAPI = ({ all = true, limit = 5 } = {}) => {
  const queryClient = useQueryClient()

  const accessoriesUsageSearchAPI = async searchTerm => {
    const queryKey = ["accessories-usage-searching", { search: searchTerm, all, limit }]

    const cached = queryClient.getQueryData(queryKey)
    if (cached) return cached

    const data = await queryClient.fetchQuery({
      queryKey,
      queryFn: () =>
        getAccessoriesUsageList({
          search: searchTerm,
          all: true,
        }),
    })

    return data
  }

  const loading =
    useIsFetching({
      queryKey: ["accessories-usage-searching"],
    }) > 0

  return { accessoriesUsageSearchAPI, loading }
}
